import axios from 'axios';
import { API_BASE_URL } from '../config/api';

/**
 * Telemetry Service for dynamic chart data
 * Separate from existing apiService to keep telemetry functionality isolated
 */
class TelemetryService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/telemetry`;
    
    // Create axios instance with common config
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000, // 30 second timeout
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    // Add request interceptor for authentication
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    
    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('Telemetry API Error:', error);
        
        if (error.response?.status === 401) {
          // Handle unauthorized - could redirect to login
          console.warn('Unauthorized access to telemetry API');
        }
        
        return Promise.reject(error);
      }
    );
    
    console.log('📊 TelemetryService initialized:', this.baseURL);
  }

  /**
   * Get available measures with metadata for chart configuration
   */
  async getAvailableMeasures() {
    try {
      console.log('📊 Fetching available measures...');
      const response = await this.api.get('/measures');
      console.log('✅ Available measures fetched:', response.data.count);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch available measures:', error);
      throw error;
    }
  }

  /**
   * Get available sensors/devices for an organization
   */
  async getAvailableSensors(organizationId) {
    try {
      console.log(`📊 Fetching sensors for organization: ${organizationId}`);
      const response = await this.api.get(`/sensors/${organizationId}`);
      console.log('✅ Sensors fetched:', response.data.count);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch sensors:', error);
      throw error;
    }
  }

  /**
   * Query telemetry data for charts
   */
  async queryChartData(params, options = {}) {
    try {
      const {
        deviceIds,
        measures,
        timeRange = '24h',
        startDate,
        endDate,
        limit = 10000,
        aggregation = 'raw',
        resolution = '1m'
      } = params;
      
      const { format = 'charts' } = options;
      
      console.log('📊 Querying chart data:', {
        devices: deviceIds?.length || 0,
        measures: measures?.length || 0,
        timeRange: startDate && endDate ? `${startDate} to ${endDate}` : timeRange,
        aggregation,
        resolution,
        hasDateRange: !!(startDate && endDate)
      });
      
      // Build request payload - only include timeRange if no specific dates provided
      const requestPayload = {
        deviceIds,
        measures,
        limit,
        aggregation,
        resolution
      };
      
      // Add time parameters - prefer specific dates over timeRange
      if (startDate && endDate) {
        requestPayload.startDate = startDate;
        requestPayload.endDate = endDate;
      } else {
        requestPayload.timeRange = timeRange;
      }
      
      console.log('📤 Final API request payload:', requestPayload);
      
      const response = await this.api.post('/chart-data', requestPayload, {
        params: { format }
      });
      
      const totalRecords = response.data.metadata?.totalRecords || 0;
      const truncationInfo = response.data.metadata?.dataTruncation;
      
      console.log('✅ Chart data received:', totalRecords, 'records');
      
      if (truncationInfo?.truncatedFromStart) {
        console.log(`📊 Data truncated from start: ${truncationInfo.originalRecords} → ${truncationInfo.returnedRecords} records (removed ${truncationInfo.truncatedRecords} oldest records)`);
        if (truncationInfo.actualStartTime) {
          console.log(`📅 Actual time range: ${truncationInfo.actualStartTime} to ${truncationInfo.requestedEndTime || 'present'}`);
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to query chart data:', error);
      throw error;
    }
  }

  /**
   * Get telemetry data summary/statistics for an organization
   */
  async getDataSummary(organizationId, timeRange = '24h') {
    try {
      console.log(`📊 Fetching data summary for org: ${organizationId}, range: ${timeRange}`);
      const response = await this.api.get(`/summary/${organizationId}`, {
        params: { timeRange }
      });
      console.log('✅ Data summary received');
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch data summary:', error);
      throw error;
    }
  }

  /**
   * Batch query multiple datasets efficiently
   */
  async batchQuery(queries) {
    try {
      console.log(`📊 Executing ${queries.length} batch queries...`);
      
      const promises = queries.map(query => this.queryChartData(query.params, query.options));
      const results = await Promise.allSettled(promises);
      
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      console.log(`✅ Batch query completed: ${successful} successful, ${failed} failed`);
      
      return results.map((result, index) => ({
        queryId: queries[index].id || index,
        success: result.status === 'fulfilled',
        data: result.status === 'fulfilled' ? result.value : null,
        error: result.status === 'rejected' ? result.reason : null
      }));
    } catch (error) {
      console.error('❌ Batch query failed:', error);
      throw error;
    }
  }

  /**
   * Get meter values for a specific sensor within a date range (for data correction)
   * Fetches data for the specified time window from the consumption report
   */
  async getSensorAllData(deviceId, organizationId, startDate = null, endDate = null) {
    try {
      console.log(`🔄 Fetching sensor data: ${deviceId} in organization: ${organizationId}`);
      console.log(`📅 Date range: ${startDate} to ${endDate}`);
      
      // Prepare request payload
      const payload = {
        deviceIds: [deviceId],
        measures: ['volume'], // Focus on volume/meter_value data (keep original logic)
        limit: 100000, // Very high limit for raw data correction (all readings)
        aggregation: 'raw', // Get raw data points for precision
        resolution: '1m' // Finest resolution available
      };
      
      // Use specific date range if provided, otherwise default to recent data
      if (startDate && endDate) {
        // For data correction, we need ALL raw readings, not aggregated data
        payload.startDate = startDate;
        payload.endDate = endDate;
        payload.aggregation = 'raw'; // Force raw data for corrections
        payload.resolution = '1s'; // Finest resolution to get all readings
        
        console.log(`📊 Using specific date range for RAW data correction: ${startDate} to ${endDate}`);
        console.log(`📊 Aggregation: ${payload.aggregation}, Resolution: ${payload.resolution}`);
      } else {
        payload.timeRange = '7d'; // Default to 7 days if no range specified
        console.log('📊 Using default 7-day range');
      }
      
      const response = await this.api.post('/chart-data', payload, {
        params: { format: 'charts' } // Get chart-formatted data
      });
      
      console.log('✅ Sensor data received:', response.data.metadata?.totalRecords || 0, 'records');
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch sensor data:', error);
      throw error;
    }
  }

  /**
   * Update multiple timestream measure values (Admin only)
   * Calls the backend bulk update API we implemented
   */
  async updateMeasureValues(updates) {
    try {
      console.log(`🔄 Updating ${updates.length} measure values...`);
      console.log('📋 Updates payload:', updates);
      console.log('📋 Sample update:', updates[0]);
      
      const response = await this.api.put('/update-measures', {
        updates: updates
      });
      
      console.log('✅ Measure values updated successfully');
      console.log('📊 Update results:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to update measure values:', error);
      console.error('📋 Error response data:', error.response?.data);
      console.error('📋 Error status:', error.response?.status);
      console.error('📋 Error headers:', error.response?.headers);
      
      // Enhanced error handling for admin operations
      if (error.response?.status === 403) {
        throw new Error('Admin access required for data correction');
      } else if (error.response?.status === 409) {
        throw new Error('Data verification failed - values may have changed');
      } else if (error.response?.data?.message) {
        throw new Error(`Backend error: ${error.response.data.message}`);
      } else if (error.response?.status === 400) {
        throw new Error(`Invalid request format - check console for details`);
      }
      
      throw error;
    }
  }

  /**
   * Get volume, digits, and scores data for filter visualization using two optimized calls
   * This avoids the table configuration issue where requesting OCR data forces InternalFormatData only
   */
  async getFilterVisualizationData(deviceId, startDate = null, endDate = null, timeRange = '24h') {
    try {
      console.log(`📊 Fetching filter visualization data for device: ${deviceId} using two optimized calls`);
      console.log(`📅 Date parameters received:`, { startDate, endDate, timeRange });
      console.log(`📅 Date validation:`, { 
        startDateTruthy: !!startDate, 
        endDateTruthy: !!endDate,
        startDateType: typeof startDate,
        endDateType: typeof endDate,
        bothDatesValid: !!(startDate && endDate)
      });
      
      // Prepare common parameters
      const baseParams = {
        deviceIds: [deviceId],
        aggregation: 'raw',
        resolution: '1s',
        limit: 50000
      };
      
      if (startDate && endDate) {
        baseParams.startDate = startDate;
        baseParams.endDate = endDate;
        console.log(`📅 Using specific date range: ${startDate} to ${endDate}`);
      } else {
        baseParams.timeRange = timeRange;
        console.log(`📅 Using timeRange: ${timeRange}`);
      }
      
      // Call 1: Get volume data (uses proper table logic - InternalFormatReporting + InternalFormatData)
      console.log('📊 Call 1: Fetching volume data with optimal table selection...');
      const volumeParams = { ...baseParams, measures: ['volume'] };
      console.log('📤 Volume call parameters:', volumeParams);
      const volumeResponse = await this.queryChartData(volumeParams);
      
      // Call 2: Get OCR data (digits, scores, status) - will use InternalFormatData only
      console.log('📊 Call 2: Fetching OCR data (digits, scores, status) from InternalFormatData...');
      const ocrParams = { ...baseParams, measures: ['digits', 'scores', 'status'] };
      console.log('📤 OCR call parameters:', ocrParams);
      const ocrResponse = await this.queryChartData(ocrParams);
      
      // Merge the data
      const volumeData = volumeResponse.data || [];
      const ocrData = ocrResponse.data || [];
      const mergedData = [...volumeData, ...ocrData];
      
      console.log('✅ Filter data fetched:', {
        volumeRecords: volumeData.length,
        ocrRecords: ocrData.length,
        totalRecords: mergedData.length
      });
      
      return {
        success: true,
        data: mergedData,
        metadata: {
          totalRecords: mergedData.length,
          volumeRecords: volumeData.length,
          ocrRecords: ocrData.length,
          measures: ['volume', 'digits', 'scores', 'status'],
          queriedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('❌ Failed to fetch filter visualization data:', error);
      throw error;
    }
  }

  /**
   * Apply digit completion filter to data
   * Handles failed reads (volume = -1) by completing missing digits using surrounding data patterns
   */
  async applyDigitCompletionFilter(inputData, filterParams = {}) {
    try {
      console.log(`🔧 Applying digit completion filter to ${inputData.length} data points`);
      
      // Aggregate data by timestamp if needed
      const aggregatedData = Array.isArray(inputData) && inputData[0]?.measure_name ? 
        this.aggregateDataByTimestamp(inputData) : inputData;
      
      const payload = {
        inputData: aggregatedData,
        filterParams: {
          confidenceThreshold: 0.8,
          lookAheadWindow: 10,
          patternAnalysisDepth: 5,
          useForwardTrend: true,
          measures: ['volume'],
          aggregated: true,
          ...filterParams
        }
      };
      
      const response = await this.api.post('/filters/digit-completion', payload);
      
      console.log('✅ Digit completion filter applied successfully:', response.data.metadata?.corrections || 0, 'corrections made');
      return response.data;
    } catch (error) {
      console.error('❌ Failed to apply digit completion filter:', error);
      console.log('🔍 Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        success: error.response?.data?.success
      });
      
      // If backend endpoint doesn't exist yet, provide client-side fallback
      if (error.response?.status === 404 || 
          error.response?.data?.success === false ||
          error.response?.data?.error?.includes("not found") ||
          error.response?.data?.error?.includes("digit-completion") ||
          error.response?.status === 400) {
        console.log('🔄 Backend digit-completion filter not available, applying client-side fallback...');
        console.log('🔍 Backend response:', error.response?.data);
        return this.applyDigitCompletionClientSide(inputData, filterParams);
      }
      
      throw error;
    }
  }
  
  /**
   * Client-side digit completion fallback implementation
   * This provides basic digit completion logic until backend implementation is ready
   */
  applyDigitCompletionClientSide(inputData, filterParams = {}) {
    console.log('🔧 Applying client-side digit completion logic...');
    
    const {
      confidenceThreshold = 0.8,
      lookAheadWindow = 10,
      patternAnalysisDepth = 5,
      useForwardTrend = true
    } = filterParams;
    
    let corrections = 0;
    const outputData = [];
    
    // Process data in chronological order (forward in time)
    const sortedData = [...inputData].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    for (let i = 0; i < sortedData.length; i++) {
      const record = sortedData[i];
      let correctedRecord = { ...record };
      
      // Handle different input formats
      let volume, digits, scores, status;
      if (record.corrected && record.original) {
        // Already processed by previous filters - use corrected values
        volume = record.corrected.volume;
        digits = record.corrected.digits;
        scores = record.corrected.scores;
        status = record.corrected.status;
      } else if (record.volume !== undefined) {
        volume = record.volume;
        digits = record.digits;
        scores = record.scores;
        status = record.status;
      } else {
        // Skip records without required fields
        outputData.push(correctedRecord);
        continue;
      }
      
      // Check if this is a failed read (volume = -1)
      if (volume === -1) {
        console.log(`🔍 Found failed read at ${record.timestamp}, attempting digit completion...`);
        
        try {
          const completedResult = this.completeDigitsForFailedRead(
            record, i, sortedData, {
              confidenceThreshold,
              lookAheadWindow,
              patternAnalysisDepth,
              useForwardTrend
            }
          );
          
          if (completedResult.success) {
            // Create corrected format similar to digit confusion filter
            correctedRecord = {
              timestamp: record.timestamp,
              device_id: record.device_id,
              original: {
                volume: volume,
                digits: digits,
                scores: scores,
                status: status
              },
              corrected: {
                volume: completedResult.volume,
                digits: completedResult.digits,
                scores: completedResult.scores || scores,
                status: status // Status typically not modified
              },
              flags: {
                volume: {
                  corrected: true,
                  reason: 'digit-completion'
                },
                digits: {
                  corrected: true,
                  reason: 'digit-completion'  
                },
                scores: {
                  corrected: false,
                  reason: 'none'
                },
                status: {
                  corrected: false,
                  reason: 'none'
                }
              }
            };
            
            corrections++;
            console.log(`✅ Completed failed read: ${volume} → ${completedResult.volume}`);
          } else {
            console.log(`⚠️ Could not complete failed read at ${record.timestamp}`);
          }
        } catch (err) {
          console.error('❌ Error completing digits:', err);
        }
      }
      
      // Always add the record (original or corrected) to output
      outputData.push(correctedRecord);
    }
    
    console.log(`✅ Client-side digit completion completed: ${corrections} failed reads completed`);
    
    return {
      outputData: outputData,
      corrections: corrections,
      metadata: {
        corrections: corrections,
        totalInputRecords: inputData.length,
        failedReadsProcessed: corrections,
        method: 'client-side-fallback'
      },
      format: 'compact_aggregated'
    };
  }
  
  /**
   * Completes digits for a single failed read using surrounding data patterns
   */
  completeDigitsForFailedRead(failedRecord, currentIndex, allData, params) {
    const { confidenceThreshold, lookAheadWindow, patternAnalysisDepth, useForwardTrend } = params;
    
    try {
      // Parse the digits array - handle string format like "[0,0,0,7,8]"
      let digitsArray;
      if (typeof failedRecord.digits === 'string') {
        digitsArray = JSON.parse(failedRecord.digits);
      } else if (Array.isArray(failedRecord.digits)) {
        digitsArray = failedRecord.digits;
      } else {
        return { success: false, reason: 'invalid-digits-format' };
      }
      
      // Parse scores array similarly
      let scoresArray;
      if (typeof failedRecord.scores === 'string') {
        scoresArray = JSON.parse(failedRecord.scores);
      } else if (Array.isArray(failedRecord.scores)) {
        scoresArray = failedRecord.scores;
      } else {
        scoresArray = new Array(digitsArray.length).fill(0);
      }
      
      // Find surrounding successful reads for pattern analysis
      const surroundingData = this.getSurroundingSuccessfulReads(currentIndex, allData, lookAheadWindow);
      
      if (surroundingData.length === 0) {
        return { success: false, reason: 'no-surrounding-data' };
      }
      
      // Complete missing or low-confidence digits
      const completedDigits = [...digitsArray];
      let hasCompletions = false;
      
      for (let digitPos = 0; digitPos < digitsArray.length; digitPos++) {
        const currentDigit = digitsArray[digitPos];
        const currentScore = scoresArray[digitPos] || 0;
        
        // Check if digit needs completion (missing or low confidence)
        if (currentDigit === -1 || currentScore < confidenceThreshold) {
          const predictedDigit = this.predictMissingDigit(
            digitPos, surroundingData, patternAnalysisDepth, useForwardTrend
          );
          
          if (predictedDigit !== null) {
            completedDigits[digitPos] = predictedDigit;
            hasCompletions = true;
            console.log(`🔧 Completed digit at position ${digitPos}: ${currentDigit} → ${predictedDigit}`);
          }
        }
      }
      
      if (!hasCompletions) {
        return { success: false, reason: 'no-completions-needed' };
      }
      
      // Convert completed digits back to volume
      const completedVolume = this.digitsArrayToVolume(completedDigits);
      
      return {
        success: true,
        volume: completedVolume,
        digits: JSON.stringify(completedDigits),
        scores: JSON.stringify(scoresArray),
        completedPositions: completedDigits.map((digit, idx) => 
          digitsArray[idx] !== digit ? idx : null
        ).filter(pos => pos !== null)
      };
      
    } catch (error) {
      console.error('❌ Error in completeDigitsForFailedRead:', error);
      return { success: false, reason: 'processing-error', error: error.message };
    }
  }
  
  /**
   * Get surrounding successful reads for pattern analysis
   */
  getSurroundingSuccessfulReads(currentIndex, allData, windowSize) {
    const surroundingData = [];
    
    // Look backward (before current failed read)
    for (let i = Math.max(0, currentIndex - windowSize); i < currentIndex; i++) {
      const record = allData[i];
      let volume = this.extractVolumeFromRecord(record);
      
      if (volume !== null && volume !== -1) {
        surroundingData.push({
          index: i,
          timestamp: record.timestamp,
          volume: volume,
          digits: this.extractDigitsFromRecord(record),
          relativePosition: i - currentIndex // negative = before
        });
      }
    }
    
    // Look forward (after current failed read)
    for (let i = currentIndex + 1; i < Math.min(allData.length, currentIndex + windowSize + 1); i++) {
      const record = allData[i];
      let volume = this.extractVolumeFromRecord(record);
      
      if (volume !== null && volume !== -1) {
        surroundingData.push({
          index: i,
          timestamp: record.timestamp,
          volume: volume,
          digits: this.extractDigitsFromRecord(record),
          relativePosition: i - currentIndex // positive = after
        });
      }
    }
    
    return surroundingData;
  }
  
  /**
   * Extract volume from a record handling different formats
   */
  extractVolumeFromRecord(record) {
    if (record.corrected && record.corrected.volume !== undefined) {
      return record.corrected.volume; // Use corrected value if available
    } else if (record.volume !== undefined) {
      return record.volume;
    } else if (record.original && record.original.volume !== undefined) {
      return record.original.volume;
    }
    return null;
  }
  
  /**
   * Extract digits array from a record handling different formats
   */
  extractDigitsFromRecord(record) {
    let digits;
    if (record.corrected && record.corrected.digits !== undefined) {
      digits = record.corrected.digits; // Use corrected digits if available
    } else if (record.digits !== undefined) {
      digits = record.digits;
    } else if (record.original && record.original.digits !== undefined) {
      digits = record.original.digits;
    } else {
      return null;
    }
    
    // Parse string format digits
    if (typeof digits === 'string') {
      try {
        return JSON.parse(digits);
      } catch (e) {
        return null;
      }
    }
    return Array.isArray(digits) ? digits : null;
  }
  
  /**
   * Predict missing digit based on surrounding data patterns
   */
  predictMissingDigit(digitPosition, surroundingData, analysisDepth, useForwardTrend) {
    if (surroundingData.length === 0) return null;
    
    // Collect digit values at this position from surrounding data
    const digitValues = [];
    
    for (const dataPoint of surroundingData) {
      if (dataPoint.digits && dataPoint.digits[digitPosition] !== undefined && dataPoint.digits[digitPosition] !== -1) {
        digitValues.push({
          digit: dataPoint.digits[digitPosition],
          relativePosition: dataPoint.relativePosition,
          volume: dataPoint.volume
        });
      }
    }
    
    if (digitValues.length === 0) return null;
    
    // Strategy 1: Use forward trend if enabled and we have forward data
    if (useForwardTrend) {
      const forwardDigits = digitValues.filter(d => d.relativePosition > 0);
      if (forwardDigits.length > 0) {
        // Use the most common digit from forward data
        const digitCounts = {};
        forwardDigits.forEach(d => {
          digitCounts[d.digit] = (digitCounts[d.digit] || 0) + 1;
        });
        
        const mostCommonDigit = Object.keys(digitCounts).reduce((a, b) => 
          digitCounts[a] > digitCounts[b] ? a : b
        );
        
        console.log(`🔍 Forward trend prediction for position ${digitPosition}: ${mostCommonDigit}`);
        return parseInt(mostCommonDigit);
      }
    }
    
    // Strategy 2: Use most common digit from all surrounding data
    const digitCounts = {};
    digitValues.forEach(d => {
      digitCounts[d.digit] = (digitCounts[d.digit] || 0) + 1;
    });
    
    const mostCommonDigit = Object.keys(digitCounts).reduce((a, b) => 
      digitCounts[a] > digitCounts[b] ? a : b
    );
    
    console.log(`🔍 Pattern-based prediction for position ${digitPosition}: ${mostCommonDigit}`);
    return parseInt(mostCommonDigit);
  }
  
  /**
   * Convert digits array back to volume number
   */
  digitsArrayToVolume(digitsArray) {
    if (!Array.isArray(digitsArray)) return 0;
    
    let volume = 0;
    for (let i = 0; i < digitsArray.length; i++) {
      const digit = digitsArray[i];
      if (digit >= 0 && digit <= 9) {
        volume = volume * 10 + digit;
      }
    }
    
    return volume;
  }
  
  /**
   * Apply digit confusion filter to data
   */
  async applyDigitConfusionFilter(inputData, filterParams = {}) {
    try {
      console.log(`🔧 Applying digit confusion filter to ${inputData.length} data points`);
      
      // Aggregate data by timestamp to reduce payload size if needed
      const aggregatedData = Array.isArray(inputData) && inputData[0]?.measure_name ? 
        this.aggregateDataByTimestamp(inputData) : inputData;
      
      const payload = {
        inputData: aggregatedData,
        filterParams: {
          confidenceThreshold: 0.7,
          windowSize: 7,
          statisticalThreshold: 2.0,
          positionWeighting: true,
          measures: ['volume'],
          aggregated: true, // Flag to indicate data is pre-aggregated
          ...filterParams
        }
      };
      
      const response = await this.api.post('/filters/digit-confusion', payload);
      
      console.log('✅ Digit confusion filter applied successfully:', response.data.metadata?.corrections || 0, 'corrections made');
      return response.data;
    } catch (error) {
      console.error('❌ Failed to apply digit confusion filter:', error);
      throw error;
    }
  }

  /**
   * Aggregate data by timestamp for more efficient filtering
   */
  aggregateDataByTimestamp(inputData) {
    const grouped = {};
    
    // Group by timestamp
    inputData.forEach(record => {
      const timestamp = record.timestamp;
      if (!grouped[timestamp]) {
        grouped[timestamp] = {
          timestamp,
          device_id: record.device_id,
          volume: null,
          digits: null,
          scores: null,
          status: null
        };
      }
      
      // Set the appropriate measure
      if (record.measure_name === 'volume') {
        grouped[timestamp].volume = record.measure_value;
      } else if (record.measure_name === 'digits') {
        grouped[timestamp].digits = record.measure_value;
      } else if (record.measure_name === 'scores') {
        grouped[timestamp].scores = record.measure_value;
      } else if (record.measure_name === 'status') {
        grouped[timestamp].status = record.measure_value;
      }
    });
    
    // Convert to array and filter out incomplete records - now requiring status as well
    return Object.values(grouped).filter(record => 
      record.volume !== null && record.digits !== null && record.scores !== null && record.status !== null
    );
  }

  /**
   * Unpack compact filter results for chart visualization
   */
  unpackFilterResults(filterResponse) {
    console.log('🔍 DEBUG - Unpacking filter results:', {
      format: filterResponse.format,
      outputDataLength: filterResponse.outputData?.length || 0,
      metadataCorrections: filterResponse.metadata?.corrections || 0,
      hasSampleRecord: !!filterResponse.outputData?.[0],
      sampleRecordStructure: filterResponse.outputData?.[0] ? Object.keys(filterResponse.outputData[0]) : []
    });
    
    if (filterResponse.format === 'compact_aggregated') {
      console.log('🔍 DEBUG - Processing compact_aggregated format');
      const originalData = [];
      const filteredData = [];
      
      let correctedCount = 0;
      
      filterResponse.outputData.forEach(record => {
        const timestamp = record.timestamp;
        const deviceId = record.device_id;
        
        // Count corrections across all measures
        if (record.flags.volume?.corrected || record.flags.digits?.corrected || 
            record.flags.scores?.corrected || record.flags.status?.corrected) {
          correctedCount++;
        }
        
        // Process all available measures: volume, digits, scores, status
        const measures = ['volume', 'digits', 'scores', 'status'];
        measures.forEach(measure => {
          if (record.original[measure] !== undefined && record.corrected[measure] !== undefined && record.flags[measure]) {
            // For chart display, we need ALL data points (whether corrected or not)
            // This creates the full filtered dataset line
            filteredData.push({
              timestamp,
              device_id: deviceId,
              measure_name: measure,
              value: record.corrected[measure], // Use corrected value (will be same as original if not corrected)
              corrected: record.flags[measure].corrected,
              originalValue: record.original[measure],
              correctionReason: record.flags[measure].reason
            });
            
            // Also create original data points for comparison if needed
            originalData.push({
              timestamp,
              device_id: deviceId,
              measure_name: measure,
              value: record.original[measure],
              corrected: false
            });
          }
        });
      });
      
      console.log('🔍 DEBUG - Compact format results:', {
        originalDataLength: originalData.length,
        filteredDataLength: filteredData.length,
        correctedPointsFound: correctedCount,
        totalInputRecords: filterResponse.outputData.length
      });
      
      return {
        originalData,
        filteredData,
        metadata: filterResponse.metadata
      };
    } else {
      console.log('🔍 DEBUG - Using original format fallback');
      
      // Check if this is actually compact data without format field (server restart needed)
      const hasCompactStructure = filterResponse.outputData?.[0]?.original && filterResponse.outputData?.[0]?.corrected;
      
      if (hasCompactStructure) {
        console.log('🔍 DEBUG - Detected compact structure without format field, processing as compact');
        // Process as compact even without format field
        const originalData = [];
        const filteredData = [];
        
        let correctedCount = 0;
        
        filterResponse.outputData.forEach(record => {
          const timestamp = record.timestamp;
          const deviceId = record.device_id;
          
          // Count corrections across all measures
          if (record.flags.volume?.corrected || record.flags.digits?.corrected || 
              record.flags.scores?.corrected || record.flags.status?.corrected) {
            correctedCount++;
          }
          
          // Process all available measures: volume, digits, scores, status
          const measures = ['volume', 'digits', 'scores', 'status'];
          measures.forEach(measure => {
            if (record.original[measure] !== undefined && record.corrected[measure] !== undefined && record.flags[measure]) {
              // For chart display, we need ALL data points (whether corrected or not)
              filteredData.push({
                timestamp,
                device_id: deviceId,
                measure_name: measure,
                value: record.corrected[measure],
                corrected: record.flags[measure].corrected,
                originalValue: record.original[measure],
                correctionReason: record.flags[measure].reason
              });
              
              originalData.push({
                timestamp,
                device_id: deviceId,
                measure_name: measure,
                value: record.original[measure],
                corrected: false
              });
            }
          });
        });
        
        console.log('🔍 DEBUG - Fallback compact results:', {
          originalDataLength: originalData.length,
          filteredDataLength: filteredData.length,
          correctedPointsFound: correctedCount,
          totalInputRecords: filterResponse.outputData.length
        });
        
        return {
          originalData,
          filteredData,
          metadata: filterResponse.metadata
        };
      } else {
        // Handle true original format
        const originalData = filterResponse.originalData || [];
        const filteredData = filterResponse.outputData.filter(record => record.measure === 'volume');
        
        console.log('🔍 DEBUG - Original format results:', {
          originalDataLength: originalData.length,
          filteredDataLength: filteredData.length,
          hasOriginalData: !!filterResponse.originalData,
          outputDataSample: filterResponse.outputData?.slice(0,2)
        });
        
        return {
          originalData,
          filteredData,
          metadata: filterResponse.metadata
        };
      }
    }
  }
  
}

// Create singleton instance
const telemetryService = new TelemetryService();

export default telemetryService; 